import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { conRespaldo } from '@/lib/campo/loUltimoQueSeSupo'
import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type {
  DestinoDeSobrante,
  ItemSobrante,
  MaterialDeLaOrden,
  MovimientoDeSobrante,
  SaldoDeSobrante,
  SobrantePorVerificar,
  SobrantesDeLaOrden,
} from '@/lib/supabase/tipos-bd'

/**
 * INVENTARIO DE SOBRANTES (0310).
 *
 * Johan: «esos materiales sobran y el técnico es el que debe reportarlos; lo
 * verifica Laura Andrea Concha, y todo lo que se verifica que sobró entra a
 * una sección nueva, inventario de sobrantes, donde se van acumulando».
 */

const num = (v: unknown) => (v === null || v === undefined ? 0 : Number(v))

/**
 * Los materiales de la orden: lo que puede sobrar. Se guarda en el teléfono
 * porque el técnico termina en la obra, muchas veces sin señal; la tarjeta lo
 * pide apenas se pinta para que ya esté cuando abra «Terminar».
 */
export function useMaterialesDeLaOrden(ordenTrabajoId: string | null) {
  return useQuery({
    queryKey: ['materiales-de-la-orden', ordenTrabajoId],
    enabled: Boolean(ordenTrabajoId),
    staleTime: 5 * 60 * 1000,
    queryFn: () =>
      conRespaldo(`materiales.${ordenTrabajoId}`, async (): Promise<MaterialDeLaOrden[]> => {
        const { data, error } = await supabase.rpc('fn_materiales_de_la_orden', {
          p_orden_trabajo_id: ordenTrabajoId!,
        })
        if (error) throw new Error(mensajeDeError(error))
        return ((data ?? []) as MaterialDeLaOrden[]).map((m) => ({
          ...m,
          id_inventario: Number(m.id_inventario),
          cantidad: num(m.cantidad),
        }))
      }),
  })
}

function useRefrescarSobrantes() {
  const cliente = useQueryClient()
  return async () => {
    await Promise.all([
      cliente.invalidateQueries({ queryKey: ['sobrantes-por-verificar'] }),
      cliente.invalidateQueries({ queryKey: ['inventario-de-sobrantes'] }),
      cliente.invalidateQueries({ queryKey: ['movimientos-de-sobrantes'] }),
      cliente.invalidateQueries({ queryKey: ['sobrantes-de-la-orden'] }),
      cliente.invalidateQueries({ queryKey: ['materiales-de-la-orden'] }),
    ])
  }
}

export function useSobrantesPorVerificar() {
  return useQuery({
    queryKey: ['sobrantes-por-verificar'],
    refetchInterval: 60 * 1000,
    queryFn: async (): Promise<SobrantePorVerificar[]> => {
      const { data, error } = await supabase.rpc('fn_sobrantes_por_verificar')
      if (error) throw new Error(mensajeDeError(error))
      return ((data ?? []) as SobrantePorVerificar[]).map((s) => ({
        ...s,
        id_inventario: Number(s.id_inventario),
        cantidad_reportada: num(s.cantidad_reportada),
      }))
    },
  })
}

export function useInventarioDeSobrantes() {
  return useQuery({
    queryKey: ['inventario-de-sobrantes'],
    queryFn: async (): Promise<SaldoDeSobrante[]> => {
      const { data, error } = await supabase.rpc('fn_inventario_de_sobrantes')
      if (error) throw new Error(mensajeDeError(error))
      return ((data ?? []) as SaldoDeSobrante[]).map((s) => ({
        ...s,
        id_inventario: Number(s.id_inventario),
        saldo: num(s.saldo),
        entradas: num(s.entradas),
        salidas: num(s.salidas),
      }))
    },
  })
}

export function useMovimientosDeSobrantes() {
  return useQuery({
    queryKey: ['movimientos-de-sobrantes'],
    queryFn: async (): Promise<MovimientoDeSobrante[]> => {
      const { data, error } = await supabase.rpc('fn_movimientos_de_sobrantes', {})
      if (error) throw new Error(mensajeDeError(error))
      return ((data ?? []) as MovimientoDeSobrante[]).map((m) => ({
        ...m,
        id_inventario: Number(m.id_inventario),
        cantidad: num(m.cantidad),
      }))
    },
  })
}

export function useSobrantesDeLaOrden(ordenTrabajoId: string | null) {
  return useQuery({
    queryKey: ['sobrantes-de-la-orden', ordenTrabajoId],
    enabled: Boolean(ordenTrabajoId),
    // El técnico lo ve en la obra: lo asignado se guarda para verlo sin señal.
    queryFn: () =>
      conRespaldo(`sobrantes-orden.${ordenTrabajoId}`, async (): Promise<SobrantesDeLaOrden | null> => {
        const { data, error } = await supabase.rpc('fn_sobrantes_de_la_orden', {
          p_orden_trabajo_id: ordenTrabajoId!,
        })
        if (error) throw new Error(mensajeDeError(error))
        return (data as SobrantesDeLaOrden | null) ?? null
      }),
  })
}

/** La oficina registra lo que sobró por el técnico (terminó antes de la pregunta o con una app vieja). */
export function useReportarSobrantes() {
  const refrescar = useRefrescarSobrantes()
  return useMutation({
    mutationFn: async (v: { ordenTrabajoId: string; items: ItemSobrante[] }) => {
      const { data, error } = await supabase.rpc('fn_reportar_sobrantes', {
        p_orden_trabajo_id: v.ordenTrabajoId,
        p_items: v.items,
      })
      if (error) throw new Error(mensajeDeError(error))
      return Number(data ?? 0)
    },
    onSuccess: refrescar,
  })
}

export function useVerificarSobrante() {
  const refrescar = useRefrescarSobrantes()
  return useMutation({
    mutationFn: async (v: { sobranteId: string; cantidad: number; nota?: string | null }) => {
      const { error } = await supabase.rpc('fn_verificar_sobrante', {
        p_sobrante_id: v.sobranteId,
        p_cantidad: v.cantidad,
        p_nota: v.nota ?? null,
      })
      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar,
  })
}

export function useSalidaDeSobrante() {
  const refrescar = useRefrescarSobrantes()
  return useMutation({
    mutationFn: async (v: {
      idInventario: number
      cantidad: number
      destino: DestinoDeSobrante
      motivo: string | null
      ordenTrabajoId?: string | null
    }) => {
      const { data, error } = await supabase.rpc('fn_salida_de_sobrante', {
        p_id_inventario: v.idInventario,
        p_cantidad: v.cantidad,
        p_destino: v.destino,
        p_motivo: v.motivo,
        p_orden_trabajo_id: v.ordenTrabajoId ?? null,
      })
      if (error) throw new Error(mensajeDeError(error))
      return Number(data ?? 0)
    },
    onSuccess: refrescar,
  })
}

/**
 * 0314 · Del inventario de sobrantes a una orden de trabajo: uno o varios
 * materiales de una vez. Todo o nada.
 */
export function useAsignarSobrantesAOrden() {
  const refrescar = useRefrescarSobrantes()
  return useMutation({
    mutationFn: async (v: { ordenTrabajoId: string; items: ItemSobrante[]; nota?: string | null }) => {
      const { data, error } = await supabase.rpc('fn_asignar_sobrantes_a_orden', {
        p_orden_trabajo_id: v.ordenTrabajoId,
        p_items: v.items,
        p_nota: v.nota ?? null,
      })
      if (error) throw new Error(mensajeDeError(error))
      return Number(data ?? 0)
    },
    onSuccess: refrescar,
  })
}

/** Las órdenes que todavía pueden recibir material: no han terminado. */
export function useOrdenesQueRecibenMaterial() {
  return useQuery({
    queryKey: ['ordenes-que-reciben-material'],
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ordenes_trabajo')
        .select('id, numero, estado, orden_servicio:ordenes_servicio(numero, cliente:clientes(nombre))')
        .in('estado', ['creada', 'programada', 'en_ejecucion'])
        .order('numero', { ascending: false })
        .limit(300)
      if (error) throw new Error(mensajeDeError(error))
      type Fila = {
        id: string
        numero: string
        estado: string
        orden_servicio: { numero: string; cliente: { nombre: string } | null } | null
      }
      return ((data ?? []) as unknown as Fila[]).map((o) => ({
        id: o.id,
        numero: o.numero,
        etiqueta: `${o.numero} · ${o.orden_servicio?.cliente?.nombre ?? o.orden_servicio?.numero ?? ''}`,
      }))
    },
  })
}

/** «12.5» y «12,5» son lo mismo; lo que no es número, nada. */
export function leerCantidad(texto: string): number | null {
  const limpio = texto.trim().replace(',', '.')
  if (!limpio) return null
  const n = Number(limpio)
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null
}

/** 2 → «2», 2.5 → «2,5». Sin ceros de relleno. */
export function cantidadLegible(n: number): string {
  return n.toLocaleString('es-CO', { maximumFractionDigits: 2 })
}
